import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  CheckCircle2,
  Clock,
  Package,
  TrendingUp,
  X,
} from 'lucide-react';
import { getAppointments, getProducts } from '../lib/api';
import { Appointment, AppointmentStatus, Product } from '../types';
import { AdminDashboardSkeleton } from '../components/AdminSkeletons';

const STATUS_BADGE: Record<AppointmentStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-200 text-gray-700',
};

interface StatCardProps {
  icon: typeof Clock;
  label: string;
  value: number;
  tone: 'amber' | 'blue' | 'green' | 'gray' | 'indigo';
}

const TONE_STYLES: Record<StatCardProps['tone'], string> = {
  amber: 'bg-amber-50 text-amber-700',
  blue: 'bg-blue-50 text-blue-700',
  green: 'bg-green-50 text-green-700',
  gray: 'bg-gray-100 text-gray-700',
  indigo: 'bg-indigo-50 text-indigo-700',
};

function StatCard({ icon: Icon, label, value, tone }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
      <div className={`p-3 rounded-lg ${TONE_STYLES[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </div>
  );
}

export function AdminPage() {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [appts, prods] = await Promise.all([getAppointments(), getProducts()]);
        setAppointments(appts);
        setProducts(prods);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const productMap = useMemo(() => {
    const map = new Map<string, Product>();
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const stats = useMemo(() => {
    const acc = { PENDING: 0, CONFIRMED: 0, COMPLETED: 0, CANCELLED: 0 } as Record<
      AppointmentStatus,
      number
    >;
    appointments.forEach((a) => {
      acc[a.status] = (acc[a.status] ?? 0) + 1;
    });
    return acc;
  }, [appointments]);

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 7);
    const horizonStr = horizon.toISOString().slice(0, 10);
    return appointments
      .filter(
        (a) =>
          (a.status === 'PENDING' || a.status === 'CONFIRMED') &&
          a.appointmentDate >= today &&
          a.appointmentDate <= horizonStr,
      )
      .sort((a, b) => a.appointmentDate.localeCompare(b.appointmentDate))
      .slice(0, 5);
  }, [appointments]);

  const recent = useMemo(() => {
    return [...appointments]
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, 5);
  }, [appointments]);

  if (loading) {
    return <AdminDashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <section>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-ink">{t('dashboard.title')}</h1>
        <p className="text-ink-soft mt-1">{t('dashboard.subtitle')}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <StatCard
          icon={Clock}
          label={t('dashboard.stats.pending')}
          value={stats.PENDING}
          tone="amber"
        />
        <StatCard
          icon={CalendarClock}
          label={t('dashboard.stats.confirmed')}
          value={stats.CONFIRMED}
          tone="blue"
        />
        <StatCard
          icon={CheckCircle2}
          label={t('dashboard.stats.completed')}
          value={stats.COMPLETED}
          tone="green"
        />
        <StatCard
          icon={X}
          label={t('dashboard.stats.cancelled')}
          value={stats.CANCELLED}
          tone="gray"
        />
        <StatCard
          icon={Package}
          label={t('dashboard.stats.products')}
          value={products.length}
          tone="indigo"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">{t('dashboard.upcoming.title')}</h2>
            </div>
            <Link
              to="/admin/appointments"
              className="text-sm text-indigo-600 hover:underline"
            >
              {t('dashboard.viewAll')}
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 text-center">
              {t('dashboard.upcoming.empty')}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {upcoming.map((appt) => (
                <li key={appt.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{appt.name}</div>
                    <div className="text-xs text-gray-500">
                      {productMap.get(appt.productId)?.title ?? appt.productId}
                    </div>
                  </div>
                  <div className="text-sm text-gray-700">
                    <div>{appt.appointmentDate}</div>
                    <div className="text-xs text-gray-500">
                      {t(`form.timeSlots.${appt.timeSlot}`)}
                    </div>
                  </div>
                  <span
                    className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${STATUS_BADGE[appt.status]}`}
                  >
                    {t(`admin.status.${appt.status.toLowerCase()}`)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">{t('dashboard.recent.title')}</h2>
            </div>
            <Link
              to="/admin/appointments"
              className="text-sm text-indigo-600 hover:underline"
            >
              {t('dashboard.viewAll')}
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="p-6 text-sm text-gray-500 text-center">
              {t('dashboard.recent.empty')}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {recent.map((appt) => (
                <li key={appt.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{appt.name}</div>
                    <div className="text-xs text-gray-500 truncate">{appt.email}</div>
                  </div>
                  <div className="text-xs text-gray-500">{appt.createdAt.slice(0, 10)}</div>
                  <span
                    className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${STATUS_BADGE[appt.status]}`}
                  >
                    {t(`admin.status.${appt.status.toLowerCase()}`)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
